import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    document.addEventListener('pointerdown', this.#onHeaderClick, true);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    document.removeEventListener('pointerdown', this.#onHeaderClick, true);
  }

  /**
   * Merci : un clic dans l'en-tete, hors du tiroir, referme le menu et leve
   * tout de suite son piege a focus.
   *
   * Chez nous l'en-tete reste lisible et cliquable menu ouvert (le tiroir
   * s'ouvre dessous, voir `styles/_drawer.scss`). Or `trapFocus` renvoie sur
   * le burger tout focus pris hors du tiroir : la recherche Doofinder
   * s'ouvrait, mais son champ perdait le focus aussitot et on ne pouvait rien
   * y taper — releve par Guillaume le 2026-10-15, en bureau. Meme risque pour
   * le compte et le panier. `#close` ne leve le piege qu'a la fin de son
   * animation, trop tard : on le leve ici. Phase de capture, pour passer avant
   * les gestionnaires des boutons.
   *
   * **A l'appui (`pointerdown`), et non au clic** — corrige le 2026-10-20, le
   * defaut etant revenu en production comme en dev. Le navigateur deplace le
   * focus des l'appui, avant le clic : le piege, encore actif, renvoyait le
   * focus du bouton de recherche sur le burger. Ensuite, soit le clic se
   * perdait, soit la couche Doofinder s'ouvrait avec un focus qui faisait la
   * navette entre son champ et le burger. Leve a l'appui, le piege est parti
   * avant que le focus ne bouge, et le clic arrive normalement.
   * @param {PointerEvent} event
   */
  #onHeaderClick = (event) => {
    const { details } = this.refs;
    if (!details.open || !(event.target instanceof Element)) return;
    if (details.contains(event.target) || !event.target.closest('#header-component')) return;

    removeTrapFocus();
    this.close();
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    // Merci : voir `takeOver` en bas de fichier.
    if (event && takeOver(this.refs.details, event)) return;

    return isShown(this.refs.details) ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    // Merci : un clic sur une entree deja depliee la replie — c'est un
    // accordeon en bureau, le « - » le dit. Le basculement natif le faisait
    // seul, en laissant `.menu-open` et `aria-expanded` derriere lui.
    if (event) {
      if (takeOver(details, event)) return;
      if (isShown(details)) return this.#close(details);
    }

    // Merci : c'est desormais le script qui ouvre, le basculement natif du
    // `<summary>` etant empeche. Pose sur un `<details>` nomme, l'attribut
    // referme aussi l'entree soeur ouverte, comme le faisait le clic — on
    // remet donc son etat annonce d'accord, sans quoi elle restait
    // `aria-expanded="true"` une fois refermee.
    if (details.name) {
      this.querySelectorAll(`details[name="${CSS.escape(details.name)}"]`).forEach((sibling) => {
        if (sibling !== details && sibling.open) reset(sibling);
      });
    }
    details.open = true;

    // Merci : cette ouverture perime le `reset` differe qu'une fermeture
    // precedente aurait laisse en attente. Voir `pendingClose` en bas de
    // fichier pour le pourquoi.
    pendingClose.delete(details);

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      // Merci : pas de piege sur un tiroir referme avant la fin de son
      // ouverture (clic sur l'en-tete, voir `#onHeaderClick`) — il garderait
      // le focus sur le burger d'un menu ferme.
      onAnimationEnd(drawer || details, () => isShown(details) && trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    // Merci : jeton de fermeture, relu dans la continuation ci-dessous.
    const token = Symbol('close');
    pendingClose.set(details, token);

    onAnimationEnd(
      drawer || details,
      () => {
        // Le tiroir a-t-il ete rouvert entre-temps ? Alors ce `reset` est
        // caduc et le jouer refermerait une ouverture legitime.
        if (pendingClose.get(details) !== token) return;
        pendingClose.delete(details);

        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Merci : fermetures dont le `reset` differe n'a pas encore ete joue.
 *
 * `#close` ne retire pas `open` tout de suite : il attend la fin de la
 * transition du panneau via `onAnimationEnd`. Or le `<details>` natif se
 * ferme des le clic, le panneau passe en `display: none` et ses transitions
 * sont **annulees** — mais l'annulation n'a lieu qu'au prochain recalcul de
 * style, donc la promesse ne se regle qu'a la prochaine interaction. Le
 * `reset` retombait ainsi sur le clic d'ouverture suivant et le defaisait :
 * ce clic-la ne montrait rien, et comme `.menu-open` restait posee sur un
 * tiroir ferme, celui d'apres affichait le panneau d'un coup, sans
 * translation. Sequence relevee : ouverture a 4047 ms, `.menu-open` a 4078,
 * les deux annulees a 4080 par le `reset` de la fermeture d'il y a 2 s.
 *
 * On date donc chaque fermeture. Une ouverture retire l'entree, et la
 * continuation qui se reveille avec un jeton perime ne fait rien.
 *
 * Un simple `if (details.open) return` n'aurait pas suffi : `close()` est
 * aussi appele au clavier et par le bouton de fermeture, sans bascule
 * native, et c'est precisement `reset` qui ferme dans ces cas-la.
 *
 * @type {WeakMap<HTMLDetailsElement, symbol>}
 */
const pendingClose = new WeakMap();

/**
 * Merci : le script, et non plus le navigateur, ouvre et ferme les `<details>`
 * du tiroir au clic — signale par Guillaume le 2026-10-14, en cliquant vite.
 *
 * Horizon laissait le `<summary>` basculer nativement et tenait son propre
 * etat a cote (`.menu-open`, `aria-expanded`). Les deux se desaccordaient :
 *
 *   - **le tiroir bloque** : un clic arrive avant le module, le `<details>`
 *     s'ouvre nativement sans `.menu-open`, donc invisible. Au clic suivant,
 *     `toggle` le voit ouvert et ferme ; rien n'etant anime, le `reset` passe
 *     sur-le-champ et retire `open`… puis le basculement natif du meme clic le
 *     rouvre. Retour a l'etat de depart, a chaque clic, indefiniment. Releve
 *     au pas a pas : `reset` a la descente du clic, `open` remis juste apres ;
 *   - **la categorie videe** : un second clic rapide sur « Mode » referme
 *     nativement son `<details>`, mais `open()` vient de reposer `.menu-open`
 *     et `menu-drawer--has-submenu-opened` — le tiroir reste en mode
 *     « categorie ouverte », sans contenu.
 *
 * D'ou : le basculement natif est empeche, l'etat est celui du script
 * (`isShown`), et un second clic sur le meme element dans les 350 ms est
 * ignore — un double-clic n'ouvre plus pour refermer aussitot. Effet de bord
 * voulu : le `<details>` reste ouvert jusqu'au `reset` de fin de transition,
 * l'animation de fermeture se joue donc en entier.
 *
 * @param {HTMLDetailsElement} details
 * @param {Event} event
 * @returns {boolean} Vrai si le clic est ignore
 */
function takeOver(details, event) {
  if (event.type !== 'click') return false;

  event.preventDefault();

  const now = performance.now();
  const last = lastClick.get(details) ?? -Infinity;
  if (now - last < DOUBLE_CLICK_GUARD) return true;

  lastClick.set(details, now);
  return false;
}

/** Delai, en millisecondes, sous lequel un second clic est ignore. */
const DOUBLE_CLICK_GUARD = 350;

/** @type {WeakMap<HTMLDetailsElement, number>} */
const lastClick = new WeakMap();

/**
 * Merci : ouvert au sens du script — l'attribut **et** l'etat annonce. Un
 * `<details>` ouvert nativement sans passer par `open()` compte comme ferme, ce
 * qui permet au clic suivant de l'ouvrir vraiment ; une entree refermee par
 * l'accordeon exclusif (`name`) aussi, malgre son `aria-expanded` perime.
 *
 * @param {HTMLDetailsElement} details
 */
function isShown(details) {
  return details.open && details.querySelector('summary')?.getAttribute('aria-expanded') === 'true';
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
